import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboardService } from '../services/dashboardService';
import { routineService } from '../services/routineService';
import { FaClock, FaExclamationCircle, FaCheckCircle, FaCalendarAlt, FaExclamationTriangle, FaTshirt, FaCheck } from 'react-icons/fa';
import CalendarOverview from '../components/CalendarOverview';

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    recentClothing: [],
    recentAccessories: [],
    activeHabits: [],
    upcomingReminders: [],
    recentExpenses: [],
    recentIncome: [],
    statistics: {
      totalClothing: 0,
      totalAccessories: 0,
      activeHabitsCount: 0,
      pendingReminders: 0,
      totalExpenses: 0,
      totalIncome: 0,
      netBalance: 0
    }
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    fetchRoutineEvents();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardService.getDashboardData();
      setDashboardData(response.data);
      
      // Transform reminders and tasks into calendar events
      const events = [
        ...response.data.upcomingReminders.map(reminder => ({
          id: reminder._id,
          title: reminder.title,
          start: new Date(reminder.dueDate),
          end: new Date(reminder.dueDate),
          allDay: true,
          type: 'reminder',
        })),
        // Add other types of events here if needed
      ];
      
      setCalendarEvents(events);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  const fetchRoutineEvents = async () => {
    try {
      const response = await routineService.getDayTypes();
      const dayTypes = response.data;
      
      let allEvents = [];
      for (const dayType of dayTypes) {
        const routinesResponse = await routineService.getRoutines(dayType._id);
        allEvents = [...allEvents, ...routinesResponse.data];
      }

      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinutes.toString().padStart(2, '0')}`;

      const current = [];
      const past = [];
      const upcoming = [];

      allEvents.forEach(routine => {
        routine.events.forEach(event => {
          if (isEventCurrent(event.startTime, event.endTime, currentTime)) {
            current.push({ ...event, dayType: routine.dayType });
          } else if (isEventPast(event.endTime, currentTime)) {
            past.push({ ...event, dayType: routine.dayType });
          } else {
            upcoming.push({ ...event, dayType: routine.dayType });
          }
        });
      });

      setCurrentEvents(current);
      setPastEvents(past.slice(-3)); // Show last 3 past events
      setUpcomingEvents(upcoming.slice(0, 3)); // Show next 3 upcoming events
    } catch (error) {
      console.error('Error fetching routine events:', error);
    }
  };

  const isEventCurrent = (start, end, current) => {
    return current >= start && current <= end;
  };

  const isEventPast = (end, current) => {
    return current > end;
  };

  const sortedReminders = dashboardData.upcomingReminders
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  const getUrgencyColor = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'text-red-600 bg-red-50';
    if (diffDays <= 1) return 'text-orange-600 bg-orange-50';
    if (diffDays <= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const formatTimeRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffHours = Math.ceil((due - now) / (1000 * 60 * 60));
    const diffDays = Math.ceil(diffHours / 24);

    if (diffHours < 0) return 'Overdue';
    if (diffHours < 24) return `${diffHours}h remaining`;
    return `${diffDays}d remaining`;
  };

  const getUnwashedClothing = () => {
    return dashboardData.recentClothing
      .filter(item => item.wearsAfterWash > 2) // Show items worn more than 2 times without washing
      .sort((a, b) => b.wearsAfterWash - a.wearsAfterWash) // Sort by most worn
      .slice(0, 5); // Show top 5
  };

  // Add currency formatter
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-6">
      {/* Timeline Section - Moved to top */}
      <div className="mb-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-6">Current Schedule</h2>
        <div className="grid grid-cols-[1fr_2fr_1fr] gap-4">
          {/* Past Events */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Previous</h3>
            {pastEvents.map(event => (
              <div
                key={event._id}
                className="p-3 rounded-lg bg-gray-50 border-l-2"
                style={{
                  backgroundColor: event.dayType?.color + '10' || '#F3F4F6',
                  borderLeftColor: event.dayType?.color || '#E5E7EB'
                }}
              >
                <div className="font-medium">{event.title}</div>
                <div className="text-xs text-gray-500">
                  {event.startTime} - {event.endTime}
                </div>
              </div>
            ))}
          </div>

          {/* Current Events */}
          <div className="border-x px-6">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Now</h3>
            <div className="space-y-3">
              {currentEvents.map(event => (
                <div
                  key={event._id}
                  className="p-4 rounded-lg bg-blue-50 border-l-4"
                  style={{
                    backgroundColor: event.dayType?.color + '20' || '#EFF6FF',
                    borderLeftColor: event.dayType?.color || '#3B82F6'
                  }}
                >
                  <div className="font-medium text-lg">{event.title}</div>
                  <div className="text-sm text-gray-600">
                    {event.startTime} - {event.endTime}
                  </div>
                  {event.description && (
                    <div className="text-sm text-gray-500 mt-1">
                      {event.description}
                    </div>
                  )}
                </div>
              ))}
              {currentEvents.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No current activities
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">Coming Up</h3>
            {upcomingEvents.map(event => (
              <div
                key={event._id}
                className="p-3 rounded-lg bg-gray-50 border-r-2"
                style={{
                  backgroundColor: event.dayType?.color + '10' || '#F3F4F6',
                  borderRightColor: event.dayType?.color || '#E5E7EB'
                }}
              >
                <div className="font-medium">{event.title}</div>
                <div className="text-xs text-gray-500">
                  {event.startTime} - {event.endTime}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h1 className="text-xl font-medium text-gray-800 mb-6">Dashboard Overview</h1>

      {/* Urgent Tasks Section */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-medium text-gray-800">
            Urgent Tasks & Reminders
          </h2>
          <Link to="/reminders" className="text-sm text-blue-500 hover:text-blue-600">
            View All
          </Link>
        </div>
        
        <div className="divide-y divide-gray-100">
          {sortedReminders.slice(0, 5).map(reminder => (
            <div key={reminder._id} className="py-2.5 flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className={`p-1.5 rounded-full ${getUrgencyColor(reminder.dueDate)}`}>
                  <FaExclamationCircle className="w-3.5 h-3.5" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800">{reminder.title}</h3>
                  <p className="text-xs text-gray-500">{reminder.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded-full text-xs ${getUrgencyColor(reminder.dueDate)}`}>
                  {formatTimeRemaining(reminder.dueDate)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Financial Overview</h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500">Total Income</p>
              <p className="text-base font-medium text-gray-800">{formatCurrency(dashboardData.statistics.totalIncome)}</p>
            </div>
            {/* Similar pattern for other stats... */}
          </div>
        </div>
        {/* Repeat similar pattern for other stat cards... */}
      </div>

      {/* Upcoming Events Section */}
      <div className="mb-8 bg-white rounded-xl shadow-md p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Upcoming Events
          </h2>
          <Link to="/events" className="text-blue-500 hover:text-blue-600 text-sm hover:underline">
            View All
          </Link>
        </div>
        
        <div className="divide-y divide-gray-100">
          {dashboardData.upcomingEvents?.slice(0, 3).map(event => (
            <div key={event._id} className="py-3 flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className="p-2 rounded-full bg-purple-50 text-purple-600">
                  <FaCalendarAlt className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">{event.title}</h3>
                  <p className="text-sm text-gray-500">{event.description}</p>
                </div>
              </div>
              <div className="text-sm text-purple-600 font-medium">
                {formatTimeRemaining(event.date)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl p-5 shadow-lg hover:-translate-y-1 transition-transform duration-200">
          <h3 className="text-white text-xl font-semibold pb-3 border-b border-blue-400 mb-4">Financial Overview</h3>
          <div className="space-y-4">
            <div>
              <p className="text-blue-100 text-sm">Total Income</p>
              <p className="text-white text-2xl font-bold">{formatCurrency(dashboardData.statistics.totalIncome)}</p>
            </div>
            <div>
              <p className="text-blue-100 text-sm">Total Expenses</p>
              <p className="text-white text-2xl font-bold">{formatCurrency(dashboardData.statistics.totalExpenses)}</p>
            </div>
            <div>
              <p className="text-blue-100 text-sm">Net Balance</p>
              <p className="text-white text-2xl font-bold">{formatCurrency(dashboardData.statistics.netBalance)}</p>
              <div className="mt-1 text-sm">
                {dashboardData.statistics.netBalance > 0 ? (
                  <span className="text-green-300">↑ 12% from last month</span>
                ) : (
                  <span className="text-red-300">↓ 8% from last month</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-xl p-5 shadow-lg hover:-translate-y-1 transition-transform duration-200">
          <h3 className="text-white text-xl font-semibold pb-3 border-b border-green-400 mb-4">Items Overview</h3>
          <div className="space-y-4">
            <div>
              <p className="text-green-100 text-sm">Total Clothing Items</p>
              <p className="text-white text-2xl font-bold">{dashboardData.statistics.totalClothing}</p>
            </div>
            <div>
              <p className="text-green-100 text-sm">Total Accessories</p>
              <p className="text-white text-2xl font-bold">{dashboardData.statistics.totalAccessories}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl p-5 shadow-lg hover:-translate-y-1 transition-transform duration-200">
          <h3 className="text-white text-xl font-semibold pb-3 border-b border-orange-400 mb-4">Activity Overview</h3>
          <div className="space-y-4">
            <div>
              <p className="text-orange-100 text-sm">Active Habits</p>
              <p className="text-white text-2xl font-bold">{dashboardData.statistics.activeHabitsCount}</p>
            </div>
            <div>
              <p className="text-orange-100 text-sm">Pending Reminders</p>
              <p className="text-white text-2xl font-bold">{dashboardData.statistics.pendingReminders}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <CalendarOverview events={calendarEvents} />

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {/* Dashboard Cards */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
              <FaTshirt className="mr-2" /> Needs Washing
            </h2>
            <Link to="/clothing" className="text-blue-500 hover:text-blue-600 text-sm hover:underline">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {getUnwashedClothing().map(item => (
              <div key={item._id} 
                   className={`p-3 flex items-center justify-between rounded-lg border-l-4 
                   ${item.wearsAfterWash > 4 ? 'border-red-500 bg-red-50' : 
                     item.wearsAfterWash > 3 ? 'border-orange-500 bg-orange-50' : 
                     'border-yellow-500 bg-yellow-50'}`}>
                <div className="flex items-center">
                  <FaExclamationTriangle className={`mr-2 
                    ${item.wearsAfterWash > 4 ? 'text-red-500' : 
                      item.wearsAfterWash > 3 ? 'text-orange-500' : 
                      'text-yellow-500'}`} />
                  <div>
                    <h3 className="font-medium text-gray-800">{item.name}</h3>
                    <p className="text-sm text-gray-600">Worn {item.wearsAfterWash} times since last wash</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  Last washed: {new Date(item.lastWashDate).toLocaleDateString()}
                </span>
              </div>
            ))}
            
            {getUnwashedClothing().length === 0 && (
              <div className="text-center text-gray-500 py-4">
                <FaCheck className="mx-auto mb-2" />
                <p>All clothes are clean!</p>
              </div>
            )}
          </div>
        </div>

        {/* Similar pattern for other cards... */}
        {/* You can create similar DashboardCard components for Reminders, Expenses, Accessories, and Habits */}
      </div>
    </div>
  );
}

// Helper component for dashboard cards
const DashboardCard = ({ title, linkTo, items, renderItem }) => (
  <div className="bg-white rounded-xl shadow-md p-5">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      <Link to={linkTo} className="text-blue-500 hover:text-blue-600 text-sm hover:underline">
        View All
      </Link>
    </div>
    <div className="space-y-3">
      {items.map(renderItem)}
    </div>
  </div>
);

export default Dashboard;
